// Mirror of backend `models/src/bi/funnel.ts`, verbatim.

import {
  biColumnFormat,
  BiDisplayFormat,
  BiDisplayFormatKind,
  formatBiCell,
  formatBiValue,
} from './format';
import { BiCell, BiQueryResult } from './result';
import { BiFunnelViz } from './viz';

/** How a funnel's rates read: a percent, one decimal as needed. */
export const kBiFunnelRateFormat: BiDisplayFormat = {
  kind: BiDisplayFormatKind.Percent,
};

/** One stage of a funnel, as drawn and as the AI quotes it. */
export interface BiFunnelStage {
  /** The measure key, or the stage dimension's value as text. */
  key: string;
  /**
   * The series name, the measure's label, or the dimension's value (its
   * label when the dimension is coded).
   */
  label: string;
  /** Raw; null when the result has no value for the stage. */
  value: number | null;
  /** The value in its column's format. */
  display: string;
  /** Of the stage before; null on the first stage, or after a zero. */
  stepRate: number | null;
  /** Of the first stage; null on the first stage, or when it is zero. */
  overallRate: number | null;
}

/** A stage dimension's value as the text `stagesBy.order` names it by. */
export function biFunnelStageKey(cell: BiCell): string {
  return cell === null ? '' : String(cell);
}

function numeric(cell: BiCell | undefined): number | null {
  if (cell === null || cell === undefined || typeof cell === 'boolean')
    return null;
  const value = Number(cell);
  return cell === '' || !Number.isFinite(value) ? null : value;
}

function rate(value: number | null, base: number | null): number | null {
  return value === null || base === null || base === 0 ? null : value / base;
}

/**
 * A funnel's stages from its result, in order: the measures of the first
 * row, or the measure per value of the stage dimension in the viz's order
 * (a value the result lacks has no value; values not in the order are not
 * drawn). Each carries its rate from the stage before and from the first.
 */
export function biFunnelStages(
  viz: BiFunnelViz,
  result: BiQueryResult,
): BiFunnelStage[] {
  const index = (key: string) =>
    result.columns.findIndex(column => column.key === key);
  const formatOf = (key: string) => {
    const column = result.columns[index(key)];
    return column ? biColumnFormat(column, viz) : undefined;
  };
  let cells: { key: string; label: string; cell: BiCell | undefined }[] = [];
  let formats: (BiDisplayFormat | undefined)[] = [];
  if (viz.stagesBy) {
    const by = index(viz.stagesBy.key);
    const series = viz.series[0];
    const at = series ? index(series.key) : -1;
    const format = series ? formatOf(series.key) : undefined;
    const valueLabels = result.columns[by]?.valueLabels;
    cells = viz.stagesBy.order.map(value => {
      const row = result.rows.find(
        entry => biFunnelStageKey(entry[by]) === value,
      );
      return {
        key: value,
        label: valueLabels?.[value] ?? value,
        cell: row?.[at],
      };
    });
    formats = cells.map(() => format);
  } else {
    const row = result.rows[0];
    cells = viz.series.map(series => {
      const column = result.columns[index(series.key)];
      return {
        key: series.key,
        label: series.name ?? column?.label.zh ?? series.key,
        cell: row?.[index(series.key)],
      };
    });
    formats = viz.series.map(series => formatOf(series.key));
  }
  const values = cells.map(entry => numeric(entry.cell));
  return cells.map((entry, at) => ({
    key: entry.key,
    label: entry.label,
    value: values[at],
    display: formatBiCell(entry.cell ?? null, formats[at]),
    stepRate: at ? rate(values[at], values[at - 1]) : null,
    overallRate: at ? rate(values[at], values[0]) : null,
  }));
}

/** A funnel rate as text: `42.5%`, or a dash for none. */
export function formatBiFunnelRate(value: number | null): string {
  return formatBiValue(value, kBiFunnelRateFormat);
}
